import { TitleCasePipe } from '@angular/common';
import { Component, computed, effect, inject, input } from '@angular/core';
import { signalHelpers } from '@core';
import { getAvsDateRange } from '@ui';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

import { StatsSupportedTypes, type WidgetDeviceStats } from '../../../models';
import { DashboardStore, getChartKey } from '../../../stores';

const TOTAL_THRESHOLD = 1000;

enum StatEntry {
  average,
  max,
  total,
  min,
}

interface StatItem {
  name: string;
  value: number;
}

@Component({
  imports: [
    TitleCasePipe,
    NgxSkeletonLoaderModule,
  ],
  selector: 'avs-device-stats',
  styleUrls: ['./device-stats.component.scss'],
  templateUrl: './device-stats.component.html',
})
export class DeviceStatsComponent {
  config = input.required<WidgetDeviceStats>();

  protected displayedItemsCount = computed(() => Object.values(this.config().displayOptions).filter(item => item).length);
  protected eStatsSupportedTypes = StatsSupportedTypes;
  protected isLoading = computed(() => !this.chartState());
  protected items = [
    {
      name: 'average',
      value: 0,
    },
    {
      name: 'max',
      value: 0,
    },
    {
      name: 'total',
      value: 0,
    },
    {
      name: 'min',
      value: 0,
    },
  ] as StatItem[];
  protected unit = computed(() => this.chartState()?.measurementUnit ?? null);

  private store = inject(DashboardStore);
  private chartPayload = computed(() => {
    const { start } = getAvsDateRange(this.config().dateRange!);

    return {
      fromDateTimeUtc: start!.toISOString(),
      point: this.config().datapointName,
      toDateTimeUtc: new Date().toISOString(),
    };
  });
  private chartKey = computed(() => getChartKey(this.config().deviceId, this.config().datapointName, this.config().dateRange));
  private chartState = computed(() => this.store.chartData(this.chartKey())());

  constructor() {
    signalHelpers.untrackedEffect([this.chartPayload], (payload) => {
      this.store.loadChartData(this.config().deviceId, payload, this.config().dateRange);
    });

    effect(() => {
      const timeseries = this.chartState()?.timeseries;

      if (!timeseries) {
        return;
      }

      const stats = this.computeStatistics(timeseries);

      this.items[StatEntry.total].value = this.wattsToKwh(stats.total);
      this.items[StatEntry.max].value = this.wattsToKwh(stats.max);
      this.items[StatEntry.min].value = this.wattsToKwh(stats.min || 0);
      this.items[StatEntry.average].value = this.wattsToKwh(stats.average || 0);
    });
  }

  private computeStatistics(timeseries: { value: number; }[]) {
    let total = 0;
    let max = 0;
    let min: number | null = null;

    timeseries.forEach(entry => {
      const value = Number(entry.value);

      total += value;
      max = Math.max(max, value);
      min = min === null ? value : Math.min(min, value);
    });

    const average = total / timeseries.length;

    return {
      average,
      max,
      min: min !== null ? this.roundToTwoDecimals(min) : null,
      total,
    };
  }

  private roundToTwoDecimals(value: number) {
    return Math.round(value * 100) / 100;
  }

  private wattsToKwh(value: number) {
    return this.roundToTwoDecimals(value / (value > TOTAL_THRESHOLD ? TOTAL_THRESHOLD : 1));
  }
}
